import { useAuthContext } from '@/auth/useAuthContext';
import { PetHealthPanel } from '@/components/PetHealthPanel';
import { PetVetsPanel } from '@/components/PetVetsPanel';
import { StatusPill } from '@/components/StatusPill';
/**
 * Customer 360 — tab content components.
 *
 * Each tab is its own React component below. They take the composite payload
 * and render a focused view. Tabs render eagerly because the data is already
 * loaded; switching feels instant. Empty states + counts everywhere.
 */
import type { Customer360Response } from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import { Calendar, FileText, ImageIcon, PawPrint, Plus, ShieldCheck, Syringe } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EmptyState } from './OverviewTab';

interface CommonProps {
  data: Customer360Response;
  currency: string;
}

// ---------- Pets ----------

export function PetsTab({ data }: CommonProps) {
  const { state } = useAuthContext();
  const capabilities = state.me?.capabilities ?? [];
  const canWriteHealth =
    capabilities.includes('pets.observations.write') ||
    capabilities.includes('pets.weights.write') ||
    capabilities.includes('*');

  if (data.pets.length === 0) return <EmptyState label="No pets on record." />;
  return (
    <section className="grid gap-3 sm:grid-cols-2">
      {data.pets.map((p) => {
        const pet = p as {
          id: string;
          name: string;
          species: string;
          breed?: string | null;
          dateOfBirth?: string | null;
          behaviorNotes?: string | null;
          medicalConditions?: string | null;
        };
        const age = pet.dateOfBirth
          ? Math.floor(
              (Date.now() - new Date(pet.dateOfBirth).getTime()) / (365 * 24 * 60 * 60 * 1000),
            )
          : null;
        return (
          <div key={pet.id} className="rounded-xl border border-border bg-surface px-4 py-3">
            <div className="flex items-start gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-secondary">
                <PawPrint className="size-5 text-muted-foreground" strokeWidth={1.6} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Link
                    to={`/customers/${(data.customer as { id: string }).id}/pets/${pet.id}`}
                    className="font-medium hover:text-primary hover:underline"
                  >
                    {pet.name}
                  </Link>
                </div>
                <p className="text-xs text-muted-foreground">
                  {pet.species}
                  {pet.breed ? ` · ${pet.breed}` : ''}
                  {age !== null ? ` · ${age}y` : ''}
                </p>
                {pet.behaviorNotes && (
                  <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">
                    {pet.behaviorNotes}
                  </p>
                )}
                {pet.medicalConditions && (
                  <p className="mt-1 line-clamp-2 text-xs text-amber-700">
                    {pet.medicalConditions}
                  </p>
                )}
                <PetHealthPanel
                  petId={pet.id}
                  petName={pet.name}
                  petSpecies={pet.species}
                  canWrite={canWriteHealth}
                />
                <PetVetsPanel petId={pet.id} />
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}

// ---------- Bookings ----------

export function BookingsTab({ data, currency }: CommonProps) {
  const [filter, setFilter] = useState<string>('all');
  const navigate = useNavigate();
  const customerId = data.customer.id;
  const rows = data.bookings;
  const filtered = rows.filter((r) => {
    if (filter === 'all') return true;
    return (r.booking as { status: string }).status === filter;
  });
  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => navigate(`/bookings/new?customerId=${customerId}`)}
            className="flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press"
          >
            <Plus className="size-3.5" strokeWidth={2} />
            New booking
          </button>
        </div>
        <EmptyState label="No bookings yet." />
      </div>
    );
  }
  return (
    <section className="rounded-xl border border-border bg-surface">
      <div className="flex items-center justify-between gap-2 border-b border-border px-5 py-3.5">
        <h2 className="text-sm font-semibold">
          Bookings <span className="num text-muted-foreground">({rows.length})</span>
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs"
          >
            <option value="all">All statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="confirmed_deposit">Deposit paid</option>
            <option value="paid">Fully paid</option>
            <option value="checked_in">Checked in</option>
            <option value="checked_out">Checked out</option>
            <option value="cancelled">Cancelled</option>
            <option value="no_show">No-show</option>
          </select>
          <button
            type="button"
            onClick={() => navigate(`/bookings/new?customerId=${customerId}`)}
            className="flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press"
          >
            <Plus className="size-3.5" strokeWidth={2} />
            New booking
          </button>
        </div>
      </div>
      <div className="divide-y divide-border">
        {filtered.map((entry) => {
          const bk = entry.booking as {
            id: string;
            bookingGroupId?: string | null;
            status: string;
            type: string;
            createdAt: string;
            totalMinor: number;
          };
          const stay = entry.stay as { checkIn?: string; checkout?: string } | null;
          const bookingHref = `/bookings/${bk.bookingGroupId || bk.id}`;
          return (
            <Link
              key={bk.id}
              to={bookingHref}
              className="flex items-center gap-3 px-5 py-3 text-sm hover:bg-secondary cursor-pointer"
            >
              <Calendar className="size-4 text-muted-foreground" strokeWidth={1.6} />
              <span className="num flex-1 text-muted-foreground">
                {stay?.checkIn
                  ? new Date(stay.checkIn).toLocaleDateString('en-GB')
                  : new Date(bk.createdAt).toLocaleDateString('en-GB')}
              </span>
              <span className="hidden sm:inline-block text-xs capitalize text-muted-foreground">
                {bk.type}
              </span>
              <StatusPill status={bk.status} />
              <span className="num w-20 text-right text-xs text-muted-foreground">
                {formatPriceMinor(bk.totalMinor ?? 0, currency)}
              </span>
            </Link>
          );
        })}
        {filtered.length === 0 && (
          <div className="px-5 py-6 text-center text-sm text-muted-foreground">
            No bookings match this filter.
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- Photos ----------

export function PhotosTab({ data }: CommonProps) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  if (data.photos.length === 0) return <EmptyState label="No photos shared yet." />;
  return (
    <section>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
        {data.photos.map((p, i) => {
          const ph = p as { id: string; thumbUrl?: string; url?: string; caption?: string };
          const src = ph.thumbUrl ?? ph.url ?? '';
          return (
            <button
              key={ph.id}
              type="button"
              onClick={() => setLightboxIdx(i)}
              className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-secondary press"
            >
              {src ? (
                // biome-ignore lint/a11y/useAltText: caption optional
                <img src={src} alt={ph.caption ?? ''} className="size-full object-cover" />
              ) : (
                <span className="grid size-full place-items-center text-muted-foreground">
                  <ImageIcon className="size-6" strokeWidth={1.5} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      {lightboxIdx !== null && (
        <button
          type="button"
          onClick={() => setLightboxIdx(null)}
          className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-6"
        >
          {(() => {
            const ph = data.photos[lightboxIdx] as {
              url?: string;
              thumbUrl?: string;
              caption?: string;
            };
            return (
              <img
                src={ph.url ?? ph.thumbUrl ?? ''}
                alt={ph.caption ?? ''}
                className="max-h-full max-w-full rounded-lg"
              />
            );
          })()}
        </button>
      )}
    </section>
  );
}

// ---------- Messages ----------

export function MessagesTab({ data }: CommonProps) {
  if (data.messages.length === 0) return <EmptyState label="No messages yet." />;
  return (
    <section className="space-y-2">
      {data.messages.map((m, i) => {
        const msg = m as {
          id: string;
          body: string;
          sender?: string;
          createdAt: string;
          bookingId?: string;
        };
        const fromCustomer = msg.sender === 'customer';
        return (
          <div
            key={msg.id ?? i}
            className={[
              'rounded-xl border px-4 py-2.5 text-sm',
              fromCustomer ? 'border-border bg-surface' : 'border-primary/30 bg-primary/5',
            ].join(' ')}
          >
            <div className="mb-1 flex items-center justify-between gap-2 text-[10.5px] text-muted-foreground">
              <span className="font-medium">{fromCustomer ? 'Customer' : 'Staff'}</span>
              <span className="num">{new Date(msg.createdAt).toLocaleString('en-GB')}</span>
            </div>
            <p className="whitespace-pre-wrap">{msg.body}</p>
          </div>
        );
      })}
    </section>
  );
}

// ---------- Payments ----------

export function PaymentsTab({ data, currency }: CommonProps) {
  if (data.payments.length === 0) return <EmptyState label="No payments on file." />;
  return (
    <section className="rounded-xl border border-border bg-surface">
      <div className="grid grid-cols-1 gap-2 border-b border-border px-5 py-3 sm:grid-cols-3 sm:gap-0">
        <Total label="Lifetime spend" amount={data.paymentsTotalsMinor.spend} currency={currency} />
        <Total label="Refunds" amount={data.paymentsTotalsMinor.refund} currency={currency} />
        <Total label="Net" amount={data.paymentsTotalsMinor.net} currency={currency} bold />
      </div>
      <div className="divide-y divide-border">
        {data.payments.map((p, i) => {
          const pay = p as {
            id: string;
            kind: string;
            direction: string;
            status: string;
            amountMinor: number;
            createdAt: string;
            source: string;
          };
          return (
            <div
              key={pay.id ?? i}
              className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-5 py-2.5 text-sm"
            >
              <span className="num text-muted-foreground text-xs">
                {new Date(pay.createdAt).toLocaleDateString('en-GB')}
              </span>
              <span className="capitalize text-foreground">
                {pay.kind.replace('_', ' ')}{' '}
                <span className="text-xs text-muted-foreground">· {pay.source}</span>
              </span>
              <span
                className={[
                  'num text-sm font-medium',
                  pay.direction === 'out' ? 'text-rose-600' : 'text-emerald-700',
                ].join(' ')}
              >
                {pay.direction === 'out' ? '−' : ''}
                {formatPriceMinor(pay.amountMinor, currency)}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Total({
  label,
  amount,
  currency,
  bold,
}: {
  label: string;
  amount: number;
  currency: string;
  bold?: boolean;
}) {
  return (
    <div>
      <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p className={['num mt-0.5 text-sm', bold ? 'font-semibold' : ''].join(' ')}>
        {formatPriceMinor(amount, currency)}
      </p>
    </div>
  );
}

// ---------- Loyalty + Referrals ----------

export function LoyaltyTab({ data }: CommonProps) {
  if (!data.loyalty.enabled) {
    return <EmptyState label="Loyalty program not enabled for this account." />;
  }
  return (
    <section className="space-y-4">
      <div className="rounded-xl border border-border bg-surface px-5 py-4">
        <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Balance
        </p>
        <p className="num mt-1 text-3xl font-semibold tracking-tight">
          {data.loyalty.balance}
          <span className="ml-1 text-base font-normal text-muted-foreground">pts</span>
        </p>
        {data.referral.code && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <p className="text-muted-foreground">Referral code:</p>
            <code className="num rounded-md bg-secondary px-2 py-0.5 font-medium">
              {data.referral.code}
            </code>
            <span className="text-xs text-muted-foreground">
              · {data.referral.redemptions} redemptions
            </span>
          </div>
        )}
      </div>
      <div className="rounded-xl border border-border bg-surface">
        <div className="border-b border-border px-5 py-3.5">
          <h2 className="text-sm font-semibold">Ledger</h2>
        </div>
        {data.loyalty.recent.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-muted-foreground">
            No loyalty activity yet.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {data.loyalty.recent.map((r, i) => {
              const row = r as {
                id: string;
                deltaPoints: number;
                reason: string;
                createdAt: string;
                note?: string | null;
              };
              return (
                <div
                  key={row.id ?? i}
                  className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-5 py-2.5 text-sm"
                >
                  <span className="num text-xs text-muted-foreground">
                    {new Date(row.createdAt).toLocaleDateString('en-GB')}
                  </span>
                  <span className="capitalize">{row.reason.replace('_', ' ')}</span>
                  <span
                    className={[
                      'num text-sm font-medium',
                      row.deltaPoints < 0 ? 'text-rose-600' : 'text-emerald-700',
                    ].join(' ')}
                  >
                    {row.deltaPoints > 0 ? '+' : ''}
                    {row.deltaPoints}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- Vaccines ----------

export function VaccinesTab({ data }: CommonProps) {
  if (data.vaccinations.length === 0) return <EmptyState label="No vaccinations recorded." />;
  return (
    <section className="rounded-xl border border-border bg-surface">
      <div className="border-b border-border px-5 py-3.5">
        <h2 className="text-sm font-semibold">
          Vaccinations{' '}
          <span className="num text-muted-foreground">({data.vaccinations.length})</span>
        </h2>
      </div>
      <div className="divide-y divide-border">
        {data.vaccinations.map((v, i) => {
          const row = v as {
            id: string;
            petName: string;
            vaccineName?: string;
            vaccineSlug?: string;
            expiresOn?: string | null;
            administeredOn?: string | null;
            status?: string;
          };
          const expired = row.expiresOn && new Date(row.expiresOn) < new Date();
          return (
            <div
              key={row.id ?? i}
              className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-5 py-2.5 text-sm"
            >
              <Syringe className="size-4 text-muted-foreground" strokeWidth={1.6} />
              <div className="min-w-0">
                <p className="font-medium">
                  {row.vaccineName ?? row.vaccineSlug ?? 'Unknown'}{' '}
                  <span className="font-normal text-muted-foreground">· {row.petName}</span>
                </p>
                {row.administeredOn && (
                  <p className="num text-xs text-muted-foreground">
                    given {new Date(row.administeredOn).toLocaleDateString('en-GB')}
                  </p>
                )}
              </div>
              {row.expiresOn && (
                <span
                  className={[
                    'num rounded-full px-2 py-0.5 text-[10.5px] font-medium',
                    expired ? 'bg-rose-100 text-rose-900' : 'bg-emerald-100 text-emerald-900',
                  ].join(' ')}
                >
                  {expired ? 'Expired' : 'Expires'}{' '}
                  {new Date(row.expiresOn).toLocaleDateString('en-GB')}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------- Notes ----------

export function NotesTab({ data }: CommonProps) {
  if (data.notes.length === 0) return <EmptyState label="No staff notes yet." />;
  return (
    <section className="space-y-2">
      {data.notes.map((n, i) => {
        const note = n as {
          id: string;
          body: string;
          pinned?: number;
          createdAt: string;
        };
        return (
          <div
            key={note.id ?? i}
            className={[
              'rounded-xl border px-4 py-3 text-sm',
              note.pinned === 1 ? 'border-amber-300 bg-amber-50' : 'border-border bg-surface',
            ].join(' ')}
          >
            <div className="mb-1 flex items-center justify-between gap-2 text-[10.5px] uppercase tracking-wide text-muted-foreground">
              <span>{note.pinned === 1 ? 'Pinned' : 'Note'}</span>
              <span className="num">{new Date(note.createdAt).toLocaleDateString('en-GB')}</span>
            </div>
            <p className="whitespace-pre-wrap">{note.body}</p>
          </div>
        );
      })}
    </section>
  );
}

// ---------- Audit ----------

export function AuditTab({ data }: CommonProps) {
  if (data.activityTimeline.length === 0)
    return <EmptyState label="No audit entries (or you don't have access)." />;
  return (
    <section className="rounded-xl border border-border bg-surface">
      <div className="border-b border-border px-5 py-3.5">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
          <ShieldCheck className="size-4" strokeWidth={1.75} />
          Audit log
        </h2>
      </div>
      <div className="divide-y divide-border">
        {data.activityTimeline.map((a, i) => {
          const row = a as {
            id: string;
            action: string;
            actorMembershipId?: string | null;
            createdAt: string;
            metadata?: string | null;
          };
          return (
            <div
              key={row.id ?? i}
              className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-5 py-2.5 text-sm"
            >
              <FileText className="size-4 text-muted-foreground" strokeWidth={1.6} />
              <span className="font-mono text-xs">{row.action}</span>
              <span className="num shrink-0 text-xs text-muted-foreground">
                {new Date(row.createdAt).toLocaleString('en-GB')}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
